import { useEffect, useRef, useState } from 'react'
import { providersAPI } from '../services/api'
import { nearestZip5 } from '../utils/zip5Locate'

/**
 * useMapArea — s106 (TestFlight b72, Dr. Rami: "The map handling needs to be updated to match the
 * search in the case send flow. Zoom in, search the area." / "They need to match"; mockup "Orella
 * Doctors Tab Send", approved 2026-10-16).
 *
 * The share step's "Search this area" (s101 Phase 4), lifted out of ShareScreen so the Doctors tab
 * runs the SAME code and the two maps cannot drift apart again:
 *   - `areaView` is where the PATIENT moved the map (DoctorsMap reports it through onViewportChange,
 *     never for the camera's own fits); while set, the map offers "Search this area";
 *   - `area` is the CENTRE being searched once they tap it ({ lat, lng, zip }), the ZIP the nearest
 *     5-digit one, worked out on the phone (utils/zip5Locate: the point never leaves it). ★ s120
 *     (Dr. Rami): the distance CHIP owns the radius; "Search this area" only moves the centre. The
 *     viewport's own radiusMiles is never a search radius (it made "Within 10 mi" read "within 1 mi");
 *   - `clearArea` is "My location" (and bumps `recenterKey`, PROD-1184).
 * MapAreaButtons draws the two buttons.
 */
// ★ s123 (Dr. Rami 2026-10-29: "Yes refresh search results auto", both maps — copy v1 C): the area searches ITSELF
//   AREA_SETTLE_MS after the patient stops moving the map (DoctorsMap reports only the patient's own moves, on idle).
//   A newer move inside the wait replaces the older one; "Search this area" is no longer drawn (MapAreaButtons).
export const AREA_SETTLE_MS = 600

export function useMapArea({ auto = true } = {}) {
  const [areaView, setAreaView] = useState(null)
  const [area, setArea] = useState(null)
  const [areaBusy, setAreaBusy] = useState(false)
  const searchThisArea = async () => {
    const v = areaView
    if (!v || areaBusy) return
    setAreaBusy(true)
    try {
      const z = await nearestZip5(v.lat, v.lng, providersAPI.zipAreas)
      setArea({ lat: v.lat, lng: v.lng, zip: (z && z.zip) || '' })
      // PROD-1420: a move made while this search was in flight is kept and searched next (a fresh object
      //   re-arms the settle timer below, whose own run found this search still busy).
      setAreaView((cur) => (cur === v ? null : cur && { ...cur }))
    } finally {
      setAreaBusy(false)
    }
  }
  // PROD-1184: "My location" also brings the map back when no area was searched (the patient only
  //   panned): the map's centre did not change, so `recenterKey` tells DoctorsMap to frame it again.
  const latest = useRef(null)
  latest.current = searchThisArea
  useEffect(() => {
    if (!auto || !areaView) return undefined
    const h = setTimeout(() => { if (latest.current) latest.current() }, AREA_SETTLE_MS)
    return () => clearTimeout(h)
  }, [auto, areaView])
  const [recenterKey, setRecenterKey] = useState(0)
  const clearArea = () => { setArea(null); setAreaView(null); setRecenterKey((k) => k + 1) }
  return { areaView, setAreaView, area, setArea, areaBusy, searchThisArea, clearArea, recenterKey, auto }
}

export default useMapArea
